import PropTypes from "prop-types";
import * as React from "react";
import debounce from "lodash/debounce";
import { translate } from "react-i18next";
import classNames from "classnames";

import { Autocomplete } from "components";
import styles from "./SearchInput.scss";

import settings from "settings";
import { playInputSound, playButtonSound } from "web2ClientAPI/web2ClientAPI";

const DEBOUNCE_INTERVAL = 200;

const propTypes = {
  isGlobalSearch: PropTypes.bool,
  autocompleteFoundByTerm: PropTypes.string.isRequired,
  autocompleteItems: PropTypes.arrayOf(PropTypes.object),
  autocompleteTotalClans: PropTypes.number,
  isAutocompleteFetching: PropTypes.bool.isRequired,
  isClanUser: PropTypes.bool.isRequired,
  searchHistory: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      tag: PropTypes.string.isRequired,
      hex_color: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired
    })).isRequired
  }).isRequired,
  term: PropTypes.string,

  onAutocompleteItemClick: PropTypes.func.isRequired,
  onHistoryItemClick: PropTypes.func.isRequired,
  onClearClick: PropTypes.func.isRequired,
  onSearchInputChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired
};

class SearchInput extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      isFocused: false,
      term: this.props.term,
      request: null,
      isAutocompleteVisible: false,
      isNotEnoughSymbols: false
    };

    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onHistoryItemClick = this.onHistoryItemClick.bind(this);
    this.onChangeDebounced = debounce(this.onChangePurified.bind(this), DEBOUNCE_INTERVAL);
    this.onClearClick = this.onClearClick.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
    this.onDocumentMouseDown = this.onDocumentMouseDown.bind(this);
    this.onEnterClick = this.onEnterClick.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.onDocumentMouseDown, true);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown, true);
    if (this.state.request && this.props.isAutocompleteFetching) {
      this.state.request.abort();
    }
  }

  UNSAFE_componentWillUpdate(nextProps) {// eslint-disable-line
    if (this.props.term !== nextProps.term && nextProps.term === '') {
      this.setState({
        term: '',
        isFocused: true,
        isAutocompleteVisible: true,
        isNotEnoughSymbols: true
      }, () => {
        this._input.blur();
      });
      this.props.onClearClick();
    }
  }

  onDocumentMouseDown(e) {
    let current = e.target;
    while (current.parentNode) {
      if (current === this._input || current === this._autocomplete) {
        return;
      }
      current = current.parentNode;
    }
    this.hideAutocomplete();
  }

  hideAutocomplete() {
    this.setState({
      isAutocompleteVisible: false
    });
  }

  onBlur() {
    this.setState({
      isFocused: false
    });
  }

  onChange(event) {
    event.preventDefault();

    const trimmedValue = event.target.value.trim();

    if (!trimmedValue) {
      this.onClearClick();
    } else {
      this.setState({
        term: trimmedValue ? event.target.value : trimmedValue
      });
    }

    playInputSound();

    this.onChangeDebounced();
  }

  onChangePurified() {
    if (this.state.request && this.props.isAutocompleteFetching) {
      this.state.request.abort();
    }
    this.setState({
      request: this.props.onSearchInputChange(this.state.term)
    });
  }

  onClearClick() {
    this.setState({
      term: '',
      isFocused: true,
      isAutocompleteVisible: true,
      isNotEnoughSymbols: true
    }, () => {
      this._input.focus();
    });
    this.props.onClearClick();
  }

  onFocus() {
    if (this.state.isFocused && this.state.isAutocompleteVisible) return;

    this.setState({
      isFocused: true,
      isAutocompleteVisible: true
    });
  }

  onKeyPress(event) {
    const isEnoughSymbols = this.state.term && this.state.term.length >= settings.search.minChars;
    if (event.key === 'Enter' && isEnoughSymbols) {
      this.hideAutocomplete();
      this.onSubmit();
    }
  }

  onSubmit() {
    this._input.blur();
    this.hideAutocomplete();
    const isNotEnoughSymbols = this._input.value.length < settings.search.minChars;
    this.setState({ isNotEnoughSymbols });
    if (!isNotEnoughSymbols) {
      this.props.onSubmit(this.state.term);
    }
  }

  onHistoryItemClick(item) {
    this.props.onHistoryItemClick(item);
    this.hideAutocomplete();
  }

  onEnterClick(event) {
    playButtonSound();
    this.onSubmit(event);
  }

  renderAutocomplete() {
    if (!this.state.isAutocompleteVisible || !this.state.term.length && !this.props.searchHistory.items.length) {
      return null;
    }

    const t = this.props.t;

    let autocompleteMessage = null;
    if (this.state.term.length < settings.search.minChars) {
      autocompleteMessage = t('Для поиска клана нужно ввести хотя бы %(count)s символа', { count: settings.search.minChars });
    } else if (this.state.term === this.props.autocompleteFoundByTerm && !this.props.autocompleteItems.length && !this.props.isAutocompleteFetching) {
      autocompleteMessage = t('Кланы с таким названием или тегом не найдены');
    }

    return <div ref={_autocomplete => this._autocomplete = _autocomplete}>
                <Autocomplete isGlobalSearch={this.props.isGlobalSearch} items={this.props.autocompleteItems} isFetching={this.props.isAutocompleteFetching} message={autocompleteMessage} searchHistory={this.props.searchHistory} term={this.state.term} totalClans={this.props.autocompleteTotalClans} onHistoryItemClick={this.onHistoryItemClick} onItemClick={this.props.onAutocompleteItemClick} onSubmit={this.onSubmit} />
            </div>;
  }

  renderControl() {
    if (!this.state.term) {
      return null;
    }
    const clearButton = <div className={styles.clear} onClick={this.onClearClick} />;
    let submitButton = null;
    if (this.state.term.length >= 2) {
      submitButton = <div className={styles.button} onClick={this.onEnterClick} />;
    }
    return <div className={styles.inputButtonsWrap}>
                {submitButton}
                {clearButton}
            </div>;
  }

  render() {
    const t = this.props.t;
    const placeholderMsg = this.state.isFocused ? '' : t('Введите тег или название клана');
    const classNameInput = classNames(styles.input, {
      [styles.isNonClanUser]: !this.props.isClanUser
    });

    return <div className={styles.container}>
                <input className={classNameInput} maxLength={settings.search.maxChars} placeholder={placeholderMsg} ref={c => this._input = c} spellCheck={false} type="text" value={this.state.term} onBlur={this.onBlur} onChange={this.onChange} onFocus={this.onFocus} onKeyPress={this.onKeyPress} />
                {this.renderAutocomplete()}
                {this.renderControl()}
            </div>;
  }
}

SearchInput.propTypes = propTypes;

export default translate()(SearchInput);